import React, { useState, useEffect, useRef } from 'react';
import { Check, Star, Zap, Leaf, HelpCircle, MapPin, Sun, Shield, ChevronDown, RefreshCw, Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import L2ServiceAgreementModal from './L2ServiceAgreementModal';
import { FEATURE_SOLAR } from '@/components/featureFlags';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };
const sans = { fontFamily: '"DM Sans", "Space Grotesk", sans-serif' };

const CACHED_PRICING = {
  DK1: { spotPrice: 69.88, distribution: 25.00, transmission: 11.50, electricityTax: 0.80 },
  DK2: { spotPrice: 68.40, distribution: 25.00, transmission: 11.50, electricityTax: 0.80 }
};

export { CACHED_PRICING };

// All 3 plan definitions for the expanded picker
const PLAN_DEFS = [
  { key: 'enkel_kvartal', billingType: 'kvartal', name: 'enkel kvartal', price: 18, billing: 'Kvartalsafregning', badge: 'BILLIGST', badgeCls: 'bg-[#2F424D] text-white', icon: Star },
  { key: 'enkel_strom', billingType: 'monthly', name: 'enkel strøm', price: 28, billing: 'Månedlig afregning', badge: 'POPULÆR', badgeCls: 'bg-[#2F424D] text-white', icon: Zap },
  { key: 'enkel_natur', billingType: 'monthly', name: 'enkel natur', price: 48, billing: 'Månedlig afregning', badge: 'NATUR 🌿', badgeCls: 'bg-green-100 text-green-800', icon: Leaf },
];

export default function L2PlanStep({
  baseBillingType, setBaseBillingType,
  wantsPostpaid, setWantsPostpaid,
  wantsNatureDonation, setWantsNatureDonation,
  subscriptions,
  zone, setZone, postalCode, setPostalCode, lookupPostal,
  dsoName, postalLoading,
  onInfoModal,
  hasSolar, onToggleSolar, manualSolarKwh, setManualSolarKwh, SOLAR_AVG_PRODUCTION_KWH,
  totalKwh = 8000,
  serviceAgreement, onActivateServiceAgreement,
  serviceAgreementLocked = false,
  onServiceHaveCharger, onServiceWantCharger, onServiceDecline,
  onEditCharger,
  householdProps, // legacy — no longer rendered here, kept for backward compat
  livePricingProducts, // from getDailyPricing — products with _pricingPeriod, typicalTotal, etc.
}) {
  const [solarConfirmOpen, setSolarConfirmOpen] = useState(false);
  const [solarInfoOpen, setSolarInfoOpen] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [tillaegOpen, setTillaegOpen] = useState(false);
  const [localPostal, setLocalPostal] = useState(postalCode || '2450');
  const postalDebounce = useRef(null);
  const isEast = zone === 'DK2';

  // Sync external postalCode changes into local input
  useEffect(() => { setLocalPostal(postalCode || ''); }, [postalCode]);

  const handlePostalChange = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 4);
    setLocalPostal(clean);
    if (clean.length === 4) {
      clearTimeout(postalDebounce.current);
      postalDebounce.current = setTimeout(() => {
        setPostalCode(clean);
        lookupPostal(clean);
      }, 400);
    }
  };

  // Resolve the currently active plan key
  const activePlanKey = baseBillingType === 'kvartal' ? 'enkel_kvartal' : (wantsNatureDonation ? 'enkel_natur' : 'enkel_strom');
  const activePlan = PLAN_DEFS.find(p => p.key === activePlanKey) || PLAN_DEFS[0];

  // Map plan keys to API product names for live pricing lookup
  const PLAN_TO_API = { enkel_kvartal: 'enkel kvartal', enkel_strom: 'enkel strøm', enkel_natur: 'enkel natur' };
  const getKwhPrice = (planKey) => {
    if (!livePricingProducts) return null;
    const apiName = PLAN_TO_API[planKey];
    const prod = livePricingProducts[apiName];
    if (!prod?.typicalTotal?.kr) return null;
    return { kr: prod.typicalTotal.kr, period: prod._pricingPeriod || '12mo' };
  };

  // handleZoneSwitch removed — zone is now derived from postal code only

  const handleSelectPlan = (plan) => {
    setBaseBillingType(plan.billingType);
    if (plan.key === 'enkel_natur') {
      setWantsNatureDonation(true);
    } else {
      setWantsNatureDonation(false);
    }
    // If switching away from monthly, disable postpaid
    if (plan.billingType === 'kvartal') {
      setWantsPostpaid(false);
    }
    setShowAllPlans(false);
  };

  return (
    <div className="space-y-3 sm:space-y-4" style={sans}>
      {/* Postal code input + DSO/zone display */}
      <div className="flex items-center justify-center gap-2 sm:gap-3">
        <div className="relative">
          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            inputMode="numeric"
            value={localPostal}
            onChange={e => handlePostalChange(e.target.value)}
            placeholder="Postnr."
            className="w-[90px] sm:w-[100px] pl-7 pr-2 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2F424D] text-center font-medium"
          />
        </div>
        {postalLoading ? (
          <span className="flex items-center gap-1 text-xs text-gray-400"><Loader2 className="w-3 h-3 animate-spin" /> Henter...</span>
        ) : dsoName ? (
          <span className="text-xs sm:text-sm text-gray-500 font-medium">{dsoName} · {zone}</span>
        ) : (
          <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-medium">{zone || 'DK2'}</span>
        )}
      </div>

      {/* ── Confirmed plan card (single plan, collapsed) ── */}
      {!showAllPlans && (
        <div className="space-y-2">
          <div className="relative rounded-lg sm:rounded-xl border border-[#2F424D]/20 bg-[#f4f7f6] p-3 sm:p-4">
            <div className="absolute -top-2 sm:-top-2.5 left-1.5 sm:left-2">
              <span className={`inline-flex items-center gap-0.5 text-[10px] sm:text-xs font-bold px-2 sm:px-2.5 py-0.5 rounded-full ${activePlan.badgeCls}`}>
                <activePlan.icon className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {activePlan.badge}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <div>
                <p className="font-bold text-sm sm:text-lg text-[#2F424D]" style={serif}>{activePlan.name}</p>
                <p className="text-xs sm:text-sm text-gray-500">{activePlan.billing}</p>
              </div>
              <div className="text-right">
                <p style={serif}><span className="text-lg sm:text-2xl font-bold text-[#2F424D]">{activePlan.price}</span> <span className="text-xs sm:text-sm font-normal text-gray-500">kr./md.</span></p>
                {(() => {
                  const kwhInfo = getKwhPrice(activePlanKey);
                  if (!kwhInfo) return null;
                  return (
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">
                      ~{kwhInfo.kr} kr./kWh
                      <span className="text-gray-300 ml-1">({kwhInfo.period === '1mo' ? 'sidste md.' : '12 md.'})</span>
                    </p>
                  );
                })()}
              </div>
            </div>

            {/* Forbrugsafregning toggle — inline, only for monthly plans */}
            {baseBillingType === 'monthly' && (
              <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-[#2F424D]/15">
                <div className="flex-1 min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-[#2F424D]">Forbrugsafregning</span>
                  <span className="text-[10px] sm:text-xs text-gray-500 ml-1">+0,02 kr./kWh</span>
                </div>
                <button onClick={() => onInfoModal('forbrug')} className="w-7 h-7 rounded-full hover:bg-[#e8e4d9] flex items-center justify-center flex-shrink-0 transition-colors">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </button>
                <Switch checked={wantsPostpaid} onCheckedChange={setWantsPostpaid}
                  className="data-[state=checked]:bg-[#2F424D] data-[state=unchecked]:bg-gray-300 scale-[0.8] sm:scale-90" />
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 mt-2.5 pt-2 border-t border-[#2F424D]/15">
              <button onClick={() => onInfoModal(activePlanKey === 'enkel_kvartal' ? 'plan_enkel_kvartal' : activePlanKey === 'enkel_natur' ? 'plan_enkel_natur' : 'plan_enkel_strom')}
                className="text-[11px] sm:text-sm text-gray-500 hover:text-gray-700 underline cursor-pointer py-1">
                Se priseksempel
              </button>
              <button onClick={() => setShowAllPlans(true)}
                className="text-[11px] sm:text-sm text-[#2F424D] hover:text-[#0e2a1c] font-semibold flex items-center gap-1 py-1">
                <RefreshCw className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Skift el-aftale
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Expanded: all 3 plans ── */}
      {showAllPlans && (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs sm:text-sm font-semibold text-[#2F424D] uppercase tracking-wider block">Vælg el-aftale</label>
            <button onClick={() => setShowAllPlans(false)} className="text-[10px] sm:text-xs text-gray-400 hover:text-gray-600">Luk</button>
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            {PLAN_DEFS.map(plan => {
              const isActive = plan.key === activePlanKey;
              return (
                <button key={plan.key} onClick={() => handleSelectPlan(plan)}
                  className={`w-full relative rounded-lg sm:rounded-xl border p-3 sm:p-4 text-left transition-all ${isActive ? 'border-[#2F424D]/30 bg-[#f4f7f6]' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="absolute -top-2 left-1.5 sm:left-2">
                    <span className={`inline-flex items-center gap-0.5 text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full ${plan.badgeCls}`}>
                      <plan.icon className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {plan.badge}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div>
                      <p className="font-bold text-xs sm:text-base text-[#2F424D]" style={serif}>{plan.name}</p>
                      <p className="text-[10px] sm:text-sm text-gray-500">{plan.billing}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <p style={serif}><span className="text-base sm:text-xl font-bold text-[#2F424D]">{plan.price}</span> <span className="text-[10px] sm:text-sm font-normal text-gray-500">kr./md.</span></p>
                        <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center ${isActive ? 'border-[#2F424D] bg-[#1a3a32]' : 'border-gray-300'}`}>
                          {isActive && <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" strokeWidth={3} />}
                        </div>
                      </div>
                      {(() => {
                        const kwhInfo = getKwhPrice(plan.key);
                        if (!kwhInfo) return null;
                        return <p className="text-[10px] text-gray-400 mt-0.5">~{kwhInfo.kr} kr./kWh</p>;
                      })()}
                    </div>
                  </div>
                  <span onClick={(e) => { e.stopPropagation(); onInfoModal(`plan_${plan.key}`); }}
                    className="text-[10px] sm:text-xs text-gray-400 hover:text-gray-600 underline mt-1.5 cursor-pointer block py-0.5">Se priseksempel</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Gratis tillæg — collapsible section */}
      <div className="space-y-1.5 sm:space-y-2">
        <button
          onClick={() => setTillaegOpen(v => !v)}
          className={`w-full flex items-center justify-between rounded-lg sm:rounded-xl border p-2.5 sm:p-3 transition-all ${tillaegOpen ? 'border-[#2F424D]/20 bg-[#f4f7f6]' : 'border-dashed border-[#2F424D]/30 bg-[#f4f7f6]/50 hover:bg-[#f4f7f6] hover:border-[#2F424D]/40'}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs sm:text-sm font-semibold text-[#2F424D] uppercase tracking-wider">Gratis tillæg</span>
            {!tillaegOpen && (
              <span className="text-[10px] sm:text-xs text-gray-500 font-normal">
                {FEATURE_SOLAR ? 'Solcelleaftale · ' : ''}Serviceaftale · Saving Mode · Laderabat – <span className="font-semibold text-[#2F424D]">0 kr.</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {!tillaegOpen && <span className="text-[10px] sm:text-xs text-[#2F424D] font-medium hidden sm:inline">Klik for at tilføje</span>}
            <ChevronDown className={`w-4 h-4 text-[#2F424D] transition-transform ${tillaegOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {tillaegOpen && (
          <>
            {/* Solcelleaftale – hidden while FEATURE_SOLAR is off */}
            {FEATURE_SOLAR && (
            <div className="rounded-lg sm:rounded-xl border border-gray-200 bg-[#f0ebe0]/30 p-2.5 sm:p-3.5">
              <div className="flex items-center gap-2 py-0.5">
                <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 flex-shrink-0" />
                <span className="text-xs sm:text-base font-medium text-[#2F424D]">Solcelleaftale</span>
                <span className="text-[10px] sm:text-sm font-bold text-[#2F424D]">0 kr./md.</span>
                <div className="flex-1" />
                <button onClick={() => setSolarInfoOpen(true)} className="w-7 h-7 sm:w-9 sm:h-9 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center flex-shrink-0"><HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hover:text-gray-600" /></button>
                <Switch checked={hasSolar === true} onCheckedChange={(v) => { if (v) setSolarConfirmOpen(true); else onToggleSolar(false); }}
                  className="data-[state=checked]:bg-[#2F424D] data-[state=unchecked]:bg-gray-300 scale-[0.8] sm:scale-90" />
              </div>
              {hasSolar && (
                <div className="pl-6 sm:pl-7 pr-0.5 pt-1.5 sm:pt-2">
                  <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                    <span className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider font-medium">Årlig produktion</span>
                    <span className="text-[10px] sm:text-xs font-bold text-[#2F424D] tabular-nums">{(manualSolarKwh ?? (SOLAR_AVG_PRODUCTION_KWH || 6000)).toLocaleString('da-DK')} kWh</span>
                  </div>
                  <Slider value={[manualSolarKwh ?? (SOLAR_AVG_PRODUCTION_KWH || 6000)]}
                    onValueChange={(v) => setManualSolarKwh(v[0])} min={1000} max={15000} step={500} className="w-full" />
                </div>
              )}
            </div>
            )}

            {/* Serviceaftale + Saving Mode + Laderabat */}
            <div className="rounded-lg sm:rounded-xl border border-gray-200 bg-[#f0ebe0]/30 p-2.5 sm:p-3.5">
              <div className="flex items-center gap-2 py-0.5">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-[#2F424D] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs sm:text-base font-medium text-[#2F424D]">Serviceaftale + Saving Mode + Laderabat</span>
                    <span className="text-[10px] sm:text-sm font-bold text-[#2F424D]">0 kr./md.</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{serviceAgreementLocked ? 'Inkluderet med din ladeboks' : 'Kræver elbil og kompatibel ladeboks'}</p>
                </div>
                <button onClick={() => setServiceModalOpen(true)} className="w-7 h-7 sm:w-9 sm:h-9 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center flex-shrink-0"><HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hover:text-gray-600" /></button>
                <Switch
                  checked={serviceAgreement === true}
                  disabled={serviceAgreementLocked}
                  onCheckedChange={(v) => {
                    if (serviceAgreementLocked) return;
                    if (v) setServiceModalOpen(true);
                    else onActivateServiceAgreement(false);
                  }}
                  className="data-[state=checked]:bg-[#2F424D] data-[state=unchecked]:bg-gray-300 disabled:opacity-70 scale-[0.8] sm:scale-90"
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Solar confirm dialog */}
      <Dialog open={solarConfirmOpen} onOpenChange={setSolarConfirmOpen}>
        <DialogContent className="max-w-md bg-white rounded-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#2F424D] text-lg"><Sun className="w-5 h-5 text-amber-500" />Solcelleaftale – 0 kr./md.</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm text-gray-700 pt-2">
            <p>Med solcelleaftalen får du en <strong className="text-gray-900">produktionsaftale uden leverandørgebyr</strong>. Overskudsstrøm afregnes automatisk.</p>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
              <div className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2F424D] mt-0.5 flex-shrink-0" strokeWidth={2.5} /><span><strong className="text-gray-900">0 kr./md.</strong> – intet leverandørtillæg</span></div>
              <div className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2F424D] mt-0.5 flex-shrink-0" strokeWidth={2.5} /><span><strong className="text-gray-900">Opsigelse:</strong> Løbende måned + 1 måned</span></div>
              <div className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2F424D] mt-0.5 flex-shrink-0" strokeWidth={2.5} /><span>Understøtter <strong className="text-gray-900">gruppe 2 og 3</strong> (øjebliks- og timeafregning)</span></div>
            </div>

            {/* Group 6 warning */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3.5">
              <div className="flex items-start gap-2">
                <span className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-0.5">6</span>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Gruppe 6 – Ikke understøttet</p>
                  <p className="text-xs text-gray-600 mt-0.5">enkel energi kan desværre ikke overtage kunder i gruppe 6 (den gamle årsbaserede nettoafregning). Kontakt Energistyrelsen for at høre om dine muligheder.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={() => { onToggleSolar(true); setSolarConfirmOpen(false); }}
                className="flex-1 bg-[#D4A62A] hover:bg-[#c29625] text-[#2F424D] font-semibold py-2.5 rounded-lg transition-colors text-sm">
                Ja, tilføj solcelleaftale
              </button>
              <button onClick={() => setSolarConfirmOpen(false)}
                className="flex-1 border border-gray-200 text-gray-600 font-medium py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                Annullér
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Solar info dialog */}
      <Dialog open={solarInfoOpen} onOpenChange={setSolarInfoOpen}>
        <DialogContent className="max-w-md bg-white rounded-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#2F424D] text-lg"><Sun className="w-5 h-5 text-amber-500" />Solcelleaftale</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm text-gray-700 pt-2">
            <p>Hos enkel energi får du en <strong className="text-gray-900">produktionsaftale til dine solceller helt uden leverandørgebyr</strong>.</p>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
              <div className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2F424D] mt-0.5 flex-shrink-0" strokeWidth={2.5} /><span><strong className="text-gray-900">0 kr./md.</strong> – intet leverandørtillæg</span></div>
              <div className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2F424D] mt-0.5 flex-shrink-0" strokeWidth={2.5} /><span><strong className="text-gray-900">Opsigelse:</strong> Løbende måned + 1 måned</span></div>
              <div className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2F424D] mt-0.5 flex-shrink-0" strokeWidth={2.5} /><span>Understøtter <strong className="text-gray-900">gruppe 2 og 3</strong></span></div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3.5">
              <div className="flex items-start gap-2">
                <span className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-0.5">6</span>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Gruppe 6 kan ikke overtages</p>
                  <p className="text-xs text-gray-600 mt-0.5">Gruppe 6 (den gamle årsbaserede nettoafregning) understøttes ikke. Kontakt Energistyrelsen for muligheder.</p>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500">Besparelsen afhænger af din produktion og din nuværende leverandørs tillæg (typisk 0,8–5 øre/kWh).</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service agreement modal */}
      <L2ServiceAgreementModal
        open={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        onConfirmHaveCharger={() => { onServiceHaveCharger?.(); setServiceModalOpen(false); }}
        onConfirmWantCharger={() => { onServiceWantCharger?.(); setServiceModalOpen(false); }}
        onDecline={() => { onServiceDecline?.(); setServiceModalOpen(false); }}
      />
    </div>
  );
}